import axios from 'axios';

// Rate limiting configuration
const RATE_LIMIT = {
  maxConcurrent: 3, // Maximum concurrent requests
  delayBetweenRequests: 1000, // 1 second delay between requests
  retryAttempts: 3,
  retryDelay: 2000 // 2 seconds initial retry delay
};

// Queue to manage concurrent requests
let activeRequests = 0;
let requestQueue = [];

// Rate limiting helper functions
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const executeWithRateLimit = async (apiCall) => {
  return new Promise((resolve, reject) => {
    const execute = async () => {
      if (activeRequests >= RATE_LIMIT.maxConcurrent) {
        // Add to queue if at max concurrent requests
        requestQueue.push(execute);
        return;
      }
      
      activeRequests++;
      
      try {
        const result = await apiCall();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        activeRequests--;
        
        // Process next request in queue after delay
        if (requestQueue.length > 0) {
          setTimeout(() => {
            const nextRequest = requestQueue.shift();
            nextRequest();
          }, RATE_LIMIT.delayBetweenRequests);
        }
      }
    };
    
    execute();
  });
};

const executeWithRetry = async (apiCall, retryCount = 0) => {
  try {
    return await executeWithRateLimit(apiCall);
  } catch (error) {
    if (retryCount < RATE_LIMIT.retryAttempts && 
        (error.response?.status === 500 || error.response?.status === 429 || error.code === 'ECONNRESET')) {
      console.log(`API call failed, retrying in ${RATE_LIMIT.retryDelay * (retryCount + 1)}ms... (attempt ${retryCount + 1}/${RATE_LIMIT.retryAttempts})`);
      await delay(RATE_LIMIT.retryDelay * (retryCount + 1)); // Exponential backoff
      return executeWithRetry(apiCall, retryCount + 1);
    }
    throw error;
  }
};

/**
 * Qualifies a prospect based on scraped data and selected service
 * @param {Object} scrapedData - Combined data from website, LinkedIn, and talking points scrapers
 * @param {Object} serviceOptions - Service configuration (serviceType, subServiceType, focus)
 * @param {Object} config - API configuration for AI calls
 * @returns {Promise<Object>} Qualification result with fitScore and reasoning
 */
// Function to get descriptive focus area text
const getDescriptiveFocusArea = (focus, serviceType, subServiceType) => {
  if (!focus) return null;
  
  // Website service focus descriptions
  if (serviceType === 'website') {
    const websiteFocusDescriptions = {
      'refresh': 'content refresh and modernization',
      'financing': 'affordable website redesign with financing options',
      'conversion': 'conversion rate optimization',
      'mobile': 'mobile responsiveness improvements',
      'branding': 'brand consistency and messaging',
      'accessibility': 'website accessibility compliance'
    };
    return websiteFocusDescriptions[focus] || focus;
  }
  
  // Onboarding service focus descriptions
  if (serviceType === 'onboarding') {
    const onboardingFocusDescriptions = subServiceType === 'apollo' ? {
      'prospecting': 'finding better-fit prospects',
      'engagement': 'engaging with prospects more effectively',
      'analytics': 'getting better insights from sales data',
      'integration': 'integrating with existing tools',
      'efficiency': 'automating repetitive prospecting tasks',
      'targeting': 'precise audience targeting'
    } : {
      'deliverability': 'improving cold email deliverability',
      'automation': 'automating cold email outreach',
      'scaling': 'scaling cold email outreach efforts',
      'personalization': 'dynamic personalization at scale',
      'analytics': 'detailed email performance tracking',
      'compliance': 'maintaining email regulation compliance'
    };
    return onboardingFocusDescriptions[focus] || focus;
  }
  
  // Cold email service focus descriptions
  if (serviceType === 'cold_email') {
    const coldEmailFocusDescriptions = {
      'good_way_to_supplement_leads': 'supplementing existing lead generation',
      'targeting': 'targeted audience outreach',
      'high_roi': 'high ROI lead generation',
      'automation': 'automated outreach processes',
      'personalization': 'personalized outreach at scale',
      'analytics': 'detailed performance tracking'
    };
    return coldEmailFocusDescriptions[focus] || focus;
  }
  
  // AEO service focus descriptions
  if (serviceType === 'aeo') {
    const aeoFocusDescriptions = {
      'ai_visibility': 'AI search engine visibility',
      'content_structure': 'AI-friendly content structure',
      'semantic_optimization': 'semantic content optimization',
      'voice_search': 'voice search optimization',
      'ai_snippets': 'AI-generated snippet optimization',
      'chatbot_optimization': 'chatbot discoverability'
    };
    return aeoFocusDescriptions[focus] || focus;
  }
  
  // Partnership service focus descriptions
  if (serviceType === 'partnership') {
    const partnershipFocusDescriptions = {
      'scaling': 'scaling development capacity',
      'expertise': 'specialized technical expertise',
      'capacity': 'handling overflow work',
      'cost_efficiency': 'cost-effective development solutions',
      'client_retention': 'maintaining client satisfaction',
      'time_to_market': 'accelerating project delivery',
      'website_management': 'ongoing website management and maintenance'
    };
    return partnershipFocusDescriptions[focus] || focus;
  }
  
  // Return original focus if no mapping found
  return focus;
};

export const qualifyProspect = async (scrapedData, serviceOptions, config, websiteUrl) => {
  try {
    // Get descriptive focus area
    const descriptiveFocusArea = getDescriptiveFocusArea(
      serviceOptions.focusArea, 
      serviceOptions.serviceType, 
      serviceOptions.subServiceType
    );
    
    console.log('Starting prospect qualification...', {
      serviceType: serviceOptions.serviceType,
      subServiceType: serviceOptions.subServiceType,
      focusArea: descriptiveFocusArea,
      websiteUrl: websiteUrl
    });

    // Validate inputs
    if (!serviceOptions || !websiteUrl) {
      throw new Error('Missing required parameters for prospect qualification');
    }

    // Build service string with focus area
    let serviceString = '';
    if (serviceOptions.serviceType === 'website') {
      serviceString = 'website redesign';
    } else if (serviceOptions.serviceType === 'onboarding') {
      const platform = serviceOptions.subServiceType === 'apollo' ? 'Apollo.io' : 'Instantly.ai';
      serviceString = `${platform} onboarding`;
    } else if (serviceOptions.serviceType === 'cold_email') {
      serviceString = 'cold email systems';
    } else if (serviceOptions.serviceType === 'aeo') {
      serviceString = 'AI Engine Optimization (AEO)';
    } else if (serviceOptions.serviceType === 'partnership') {
      serviceString = 'web development partnership';
    }
    
    // Add descriptive focus area if available
    if (descriptiveFocusArea) {
      serviceString += ` - ${descriptiveFocusArea}`;
    }
    
    // Make API call to new fit score endpoint
    const qualificationResult = await makeFitScoreAPICall(websiteUrl, serviceString, serviceOptions);
    
    console.log('Prospect qualification completed:', {
      fitScore: qualificationResult.fit_score,
      reasoning: qualificationResult.reasoning?.substring(0, 100) + '...'
    });
    
    return {
      success: true,
      fitScore: qualificationResult.fit_score,
      reasoning: qualificationResult.reasoning,
      keyIndicators: qualificationResult.key_indicators || [],
      confidence: qualificationResult.confidence || 'medium',
      potentialValue: qualificationResult.potential_value || '',
      serviceType: serviceOptions.serviceType,
      subServiceType: serviceOptions.subServiceType,
      qualifiedAt: new Date().toISOString()
    };
    
  } catch (error) {
    console.error('Error in prospect qualification:', error);
    return {
      success: false,
      error: error.message,
      fitScore: 0,
      reasoning: `Qualification failed: ${error.message}`
    };
  }
};

/**
 * Builds the AI prompt for prospect qualification
 * @param {Object} scrapedData - Combined scraped data
 * @param {Object} serviceOptions - Service configuration
 * @returns {string} Formatted prompt for AI
 */
const buildQualificationPrompt = (scrapedData, serviceOptions) => {
  const { serviceType, subServiceType, focus } = serviceOptions;
  
  // Get service-specific qualification criteria
  const serviceCriteria = getServiceQualificationCriteria(serviceType, subServiceType, focus);
  
  // Build data summary
  let dataContext = '';
  
  // Add website data
  if (scrapedData.content) {
    dataContext += `\n--- WEBSITE DATA ---\n`;
    dataContext += `Company: ${scrapedData.company || 'Unknown'}\n`;
    dataContext += `Title: ${scrapedData.title || 'N/A'}\n`;
    dataContext += `Meta Description: ${scrapedData.metaDescription || 'N/A'}\n`;
    dataContext += `Content: ${scrapedData.content.substring(0, 1500)}${scrapedData.content.length > 1500 ? '...' : ''}\n`;
  }
  
  // Add LinkedIn data
  if (scrapedData.linkedInData) {
    dataContext += `\n--- LINKEDIN DATA ---\n`;
    dataContext += `Name: ${scrapedData.linkedInData.name || 'N/A'}\n`;
    dataContext += `Headline: ${scrapedData.linkedInData.headline || 'N/A'}\n`;
    dataContext += `Summary: ${scrapedData.linkedInData.summary || 'N/A'}\n`;
    dataContext += `Experience: ${scrapedData.linkedInData.experience || 'N/A'}\n`;
  }
  
  // Add talking points data
  if (scrapedData.talkingPointsData) {
    dataContext += `\n--- TALKING POINTS DATA ---\n`;
    dataContext += `Website Name: ${scrapedData.talkingPointsData.websiteName || 'N/A'}\n`;
    dataContext += `Opportunities: ${scrapedData.talkingPointsData.opportunitiesText || 'N/A'}\n`;
  }
  
  return `You are a business development expert who qualifies prospects for ${serviceCriteria.serviceName} services.

Your task is to analyze the provided prospect data and determine how good of a fit they are for our ${serviceCriteria.serviceName} service.

${serviceCriteria.description}

QUALIFICATION CRITERIA:
${serviceCriteria.criteria}

IMPORTANT QUALIFICATION APPROACH:
- PRIORITIZE industry potential and business model fit over explicit signals of need
- A company can be an excellent fit even if they don't show obvious signs of needing the service
- Focus on whether they're in an industry where this service provides significant value
- Consider their business model, size, and budget capacity as primary indicators
- Treat explicit signals (like outdated websites, sales challenges) as bonus indicators, not requirements

SCORING SCALE (0-10):
- 9-10: Excellent fit - Industry where service is highly valuable + can afford it
- 7-8: Good fit - Industry fit with some positive indicators
- 5-6: Moderate fit - Potential industry fit but unclear business model or budget
- 3-4: Poor fit - Industry where service has limited value or budget concerns
- 0-2: Very poor fit - Industry mismatch or clear disqualifiers

PROSPECT DATA TO ANALYZE:
${dataContext}

IMPORTANT: You must respond with valid JSON only. No additional text or formatting.

Respond with this exact JSON structure:
{
  "fitScore": [number from 0-10],
  "reasoning": "[2-3 sentence explanation of the score based on specific evidence from the data]",
  "keyIndicators": ["indicator1", "indicator2", "indicator3"],
  "concerns": ["concern1", "concern2"] or [],
  "confidence": "[high/medium/low based on data quality]"
}`;
};

/**
 * Gets service-specific qualification criteria
 * @param {string} serviceType - Type of service (website, onboarding, cold_email)
 * @param {string} subServiceType - Sub-service type (apollo, instantly)
 * @param {string} focus - Service focus area
 * @returns {Object} Service criteria object
 */
const getServiceQualificationCriteria = (serviceType, subServiceType, focus) => {
  if (serviceType === 'website') {
    return {
      serviceName: 'Website Redesign',
      description: 'We provide professional website redesign services focusing on modern design, conversion optimization, and user experience improvements.',
      criteria: `
- Industry where professional web presence is critical for business success (e.g., professional services, SaaS, e-commerce, consulting, agencies)
- Business model suggests they can afford $5K-$50K website projects
- Company shows signs of growth or investment in marketing
- Website appears outdated, unprofessional, or has poor design (bonus indicator)
- Evidence of current marketing efforts or lead generation needs (bonus indicator)
- Mobile responsiveness issues or poor user experience (bonus indicator)
- Conversion optimization opportunities visible (bonus indicator)
- Brand inconsistency or messaging problems (bonus indicator)`
    };
  }
  
  if (serviceType === 'onboarding') {
    const platform = subServiceType === 'apollo' ? 'Apollo.io' : 'Instantly.ai';
    return {
      serviceName: `${platform} Onboarding`,
      description: `We help businesses set up and optimize their ${platform} systems for maximum lead generation and sales efficiency.`,
      criteria: `
- Industry where B2B sales and systematic prospecting is valuable (e.g., SaaS, consulting, agencies, professional services, technology)
- Business model suggests B2B sales or outreach needs
- Team size suggests they need systematic prospecting
- Budget indicators suggesting they can invest in sales tools
- Company shows signs of active sales or lead generation efforts (bonus indicator)
- Evidence of current sales challenges or growth goals (bonus indicator)
- Signs they're not maximizing their current sales tools (bonus indicator)
- Growth stage where systematic prospecting would help (bonus indicator)`
    };
  }
  
  if (serviceType === 'cold_email') {
    return {
      serviceName: 'Cold Email System',
      description: 'We implement comprehensive cold email systems to generate qualified leads and supplement existing marketing efforts.',
      criteria: `
- Industry where cold email is effective and appropriate (e.g., B2B services, SaaS, consulting, agencies, professional services)
- Business model suggests B2B lead generation needs
- Team structure suggests they can handle increased leads
- Budget indicators for marketing/sales investments
- Clear target audience that can be reached via email
- Evidence of current marketing or sales efforts (bonus indicator)
- Signs they need to supplement existing lead sources (bonus indicator)
- Growth stage where systematic outreach would help (bonus indicator)`
    };
  }
  
  if (serviceType === 'aeo') {
    return {
      serviceName: 'AEO (AI Engine Optimization)',
      description: 'We provide AI Engine Optimization services to help websites rank better in AI-powered search results and chatbot recommendations.',
      criteria: `
- Industry where online visibility and search presence is critical (e.g., e-commerce, SaaS, professional services, consulting, agencies, local businesses)
- Business model suggests they rely on organic search traffic or online discovery
- Company shows signs of investment in digital marketing or SEO
- Website content that could benefit from AI optimization (bonus indicator)
- Evidence of current SEO or content marketing efforts (bonus indicator)
- Signs they're not maximizing their AI search visibility (bonus indicator)
- Growth stage where improved AI discoverability would drive business results (bonus indicator)
- Target audience that uses AI search tools and chatbots (bonus indicator)`
    };
  }
  
  if (serviceType === 'partnership') {
    return {
      serviceName: 'Web Development Partnership',
      description: 'We partner with agencies and businesses to provide white-label web development services, helping them scale without hiring overhead.',
      criteria: `
- Industry where web development partnerships are valuable (e.g., digital agencies, marketing agencies, consultancies, business service providers)
- Business model suggests they serve clients who need web development services
- Company shows signs of growth or client demand that exceeds current capacity
- Budget indicators suggesting they can invest in partnership services
- Evidence of current client work or service delivery (bonus indicator)
- Signs of capacity constraints or scaling challenges (bonus indicator)
- Multiple concurrent projects or tight deadlines (bonus indicator)
- Need for specialized expertise or technical capabilities (bonus indicator)
- White-label or partnership-friendly business model (bonus indicator)`
    };
  }
  
  return {
    serviceName: 'Business Service',
    description: 'General business service qualification.',
    criteria: 'General business fit indicators'
  };
};

/**
 * Makes the API call to the new fit score endpoint
 * @param {string} websiteUrl - The website URL to analyze
 * @param {string} serviceString - The service type with focus area
 * @param {Object} serviceOptions - Service configuration options
 * @returns {Promise<Object>} Fit score result
 */
const makeFitScoreAPICall = async (websiteUrl, serviceString, serviceOptions = {}) => {
  const apiCall = async () => {
    // Build request payload
    const requestPayload = {
      url: websiteUrl,
      service: serviceString
    };

    // Add optional fields for website redesign services
    if (serviceOptions.serviceType === 'website') {
      requestPayload.service_category = "web development";
      requestPayload.service_subcategory = "website design, redesign, maintenance";
      requestPayload.specialization = "conversion-focused custom websites; CMS integrations (WordPress, Webflow, Squarespace, Wix)";
      requestPayload.target_market = "businesses seeking growth and scalable systems";
      requestPayload.unique_value_proposition = "revenue-driven websites and systems designed to drive measurable ROI and conversions";
      requestPayload.payment_structure = "0% financing available up to 12 months";
      requestPayload.technical_requirements = [
        "CMS platform (WordPress, Webflow, Squarespace, Wix) compatibility",
        "CRM/email automation integration for sales and outreach systems"
      ];
      requestPayload.technology_maturity = "basic digital presence implied (existing site can be redesigned or maintained)";
      requestPayload.typical_results = [
        "improved conversions",
        "optimized operations",
        "measurable ROI"
      ];
      requestPayload.success_metrics = [
        "conversion rate",
        "revenue",
        "operational efficiency"
      ];
      requestPayload.differentiators = [
        "0% financing up to 12 months",
        "focus on revenue-driven solutions",
        "experience (9+ years)",
        "communication and on-time deliverables",
        "CMS flexibility across multiple platforms"
      ];
    }

    // Add optional fields for cold email systems
    if (serviceOptions.serviceType === 'cold_email') {
      requestPayload.service_category = "AI & automation consulting";
      requestPayload.service_subcategory = "Email automation";
      requestPayload.specialization = "Automated cold email systems (personalization, sequencing, deliverability)";
      requestPayload.unique_value_proposition = "streamline lead generation and enhance engagement through personalized, data-driven cold email campaigns";
      requestPayload.typical_results = [
        "increased lead generation",
        "higher engagement rates",
        "time savings on outreach",
        "improved email deliverability"
      ];
      requestPayload.success_metrics = [
        "open rate",
        "reply rate",
        "response-to-lead conversion",
        "bounce rate",
        "unsubscribe rate"
      ];
      requestPayload.case_study_examples = [
        "Workforce Nexus — boosted lead generation with targeted email strategies",
        "Nerd Stack — leveraged automation and AI to achieve scalable growth and improved client engagement"
      ];
      requestPayload.differentiators = [
        "personalized, data-driven sequences",
        "deliverability and inbox placement focus",
        "integration with CRM and analytics for closed-loop optimization"
      ];
      requestPayload.ideal_industries = [
        "HR services",
        "software development",
        "general business"
      ];
      requestPayload.technical_requirements = [
        "existing CRM integration"
      ];
      requestPayload.prerequisites = [
        "contact list or lead data",
        "email domain and DNS access for SPF/DKIM/DMARC setup",
        "baseline outreach goals and messaging"
      ];
      requestPayload.team_size_needed = "1-3 stakeholders";
      requestPayload.typical_project_duration = "4-8 weeks";
    }

    // Add optional fields for client onboarding services
    if (serviceOptions.serviceType === 'onboarding') {
      requestPayload.service_category = "AI & automation consulting";
      requestPayload.service_subcategory = "AI onboarding & integration";
      requestPayload.specialization = "Onboarding clients onto Instantly.ai and Apollo.io (setup, training, integration)";
      requestPayload.unique_value_proposition = "rapidly deploy Instantly.ai and Apollo.io to accelerate prospecting and sales workflows with deliverability-first setups, tailored integrations, and hands-on training";
      requestPayload.typical_results = [
        "faster prospecting workflows",
        "higher open and reply rates",
        "reduced time-to-first-outreach",
        "better CRM data hygiene"
      ];
      requestPayload.success_metrics = [
        "time-to-first-outreach",
        "open rate",
        "reply rate",
        "qualified leads generated",
        "system adoption rate",
        "data quality improvements"
      ];
      requestPayload.case_study_examples = [
        "Workforce Nexus — boosted lead generation with targeted email strategies",
        "Nerd Stack — leveraged automation and AI to achieve scalable growth and improved client engagement"
      ];
      requestPayload.differentiators = [
        "hands-on Instantly.ai and Apollo.io setup and configuration",
        "deliverability tuning (SPF/DKIM/DMARC, tracking domains, inbox rotation)",
        "custom integration with CRM and email systems",
        "training and playbooks for sales teams"
      ];
      requestPayload.ideal_industries = [
        "HR services",
        "software development",
        "general business"
      ];
      requestPayload.technical_requirements = [
        "CRM access for integration",
        "email domain and DNS access for deliverability setup",
        "mailbox access (e.g., Google Workspace or Microsoft 365)"
      ];
      requestPayload.prerequisites = [
        "Instantly.ai and Apollo.io accounts (or willingness to provision them)",
        "contact database or lead sources",
        "clear ICP, outreach objectives, and KPIs"
      ];
      requestPayload.team_size_needed = "1-3 stakeholders";
      requestPayload.typical_project_duration = "2-6 weeks";
    }

    const response = await axios.post('https://tp.pressupwards.com/analyze-service-fit', requestPayload, {
      headers: {
        'Authorization': 'Bearer your-secret-api-key-12345',
        'Content-Type': 'application/json'
      },
      timeout: 60000
    });

    console.log('Fit score API response:', response.data);
    
    // Validate response
    if (!response.data || typeof response.data.fit_score !== 'number') {
      throw new Error('Invalid response from fit score API');
    }
    
    return response.data;
  };
  
  try {
    return await executeWithRetry(apiCall);
  } catch (error) {
    console.error('Fit score API call failed after retries:', error.message);
    throw new Error(`Fit score API failed: ${error.message}`);
  }
};

/**
 * Legacy function - kept for backward compatibility
 * Makes the API call to get prospect qualification
 * @param {string} prompt - The qualification prompt
 * @param {Object} config - API configuration
 * @returns {Promise<Object>} Parsed qualification result
 */
const makeQualificationAPICall = async (prompt, config) => {
  const { endpoint, apiKey, model, ollamaEndpoint, useNoThink } = config;
  
  let apiUrl;
  let headers;
  let requestBody;
  
  // Configure API call based on endpoint
  if (endpoint === 'openai') {
    apiUrl = 'https://api.openai.com/v1/chat/completions';
    headers = {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    };
    requestBody = {
      model: model,
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.1, // Low temperature for consistent scoring
      max_tokens: 500
    };
  } else if (endpoint === 'openrouter') {
    apiUrl = 'https://openrouter.ai/api/v1/chat/completions';
    headers = {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
      'HTTP-Referer': 'https://knowtoconnect.com',
      'X-Title': 'KnowToConnect Prospect Qualifier'
    };
    requestBody = {
      model: model,
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.1,
      max_tokens: 500
    };
  } else if (endpoint === 'ollama') {
    const baseUrl = ollamaEndpoint?.replace(/\/$/, '') || 'http://localhost:11434';
    apiUrl = `${baseUrl}/api/chat`;
    headers = { 'Content-Type': 'application/json' };
    requestBody = {
      model: model,
      messages: [{ role: 'user', content: prompt }],
      stream: false,
      options: { temperature: 0.1 }
    };
  } else if (endpoint === 'custom') {
    // Assume OpenAI-compatible API
    apiUrl = config.customEndpoint || 'https://api.openai.com/v1/chat/completions';
    headers = {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    };
    requestBody = {
      model: model,
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.1,
      max_tokens: 500
    };
  } else {
    throw new Error(`Unsupported endpoint: ${endpoint}`);
  }
  
  // Add /nothink suffix if enabled
  if (useNoThink && (endpoint === 'openrouter' || endpoint === 'custom')) {
    apiUrl += '/nothink';
  }
  
  console.log('Making qualification API call to:', apiUrl.replace(apiKey || '', '[REDACTED]'));
  
  const response = await axios.post(apiUrl, requestBody, {
    headers,
    timeout: 30000
  });
  
  // Extract response content based on endpoint
  let content;
  if (endpoint === 'ollama') {
    content = response.data.message?.content;
  } else {
    content = response.data.choices?.[0]?.message?.content;
  }
  
  if (!content) {
    throw new Error('No content received from API');
  }
  
  // Parse JSON response
  try {
    const parsed = JSON.parse(content.trim());
    
    // Validate required fields
    if (typeof parsed.fitScore !== 'number' || parsed.fitScore < 0 || parsed.fitScore > 10) {
      throw new Error('Invalid fitScore in response');
    }
    
    if (!parsed.reasoning || typeof parsed.reasoning !== 'string') {
      throw new Error('Missing or invalid reasoning in response');
    }
    
    return {
      fitScore: Math.round(parsed.fitScore * 10) / 10, // Round to 1 decimal
      reasoning: parsed.reasoning,
      keyIndicators: parsed.keyIndicators || [],
      concerns: parsed.concerns || [],
      confidence: parsed.confidence || 'medium'
    };
    
  } catch (parseError) {
    console.error('Failed to parse qualification response:', content);
    throw new Error(`Invalid JSON response from AI: ${parseError.message}`);
  }
};

/**
 * Validates if prospect qualification is needed based on available data
 * @param {Object} scrapedData - Scraped data to check
 * @returns {boolean} True if qualification should be performed
 */
export const shouldQualifyProspect = (scrapedData) => {
  return !!(scrapedData?.content || scrapedData?.linkedInData || scrapedData?.talkingPointsData);
};

/**
 * Gets a human-readable fit level based on fitScore
 * @param {number} fitScore - Score from 0-10
 * @returns {string} Fit level description
 */
export const getFitLevel = (fitScore) => {
  if (fitScore >= 9) return 'Excellent';
  if (fitScore >= 7) return 'Good';
  if (fitScore >= 5) return 'Moderate';
  if (fitScore >= 3) return 'Poor';
  return 'Very Poor';
};

/**
 * Gets fit level color for UI display
 * @param {number} fitScore - Score from 0-10
 * @returns {string} CSS color class or hex color
 */
export const getFitLevelColor = (fitScore) => {
  if (fitScore >= 9) return '#22c55e'; // Green
  if (fitScore >= 7) return '#84cc16'; // Light green
  if (fitScore >= 5) return '#eab308'; // Yellow
  if (fitScore >= 3) return '#f97316'; // Orange
  return '#ef4444'; // Red
};